import React, { useState } from 'react';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiSearch, FiShoppingBag, FiMenu, FiX, FiUser, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useStore } from '../../context/StoreContext';

const Navbar = () => {
  const { cart, user, profile, products } = useStore();
  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const navigate = useNavigate();

  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > 100) {
      setIsScrolled(true);
      if (latest > previous && !isMobileMenuOpen) {
        setHidden(true);
      } else {
        setHidden(false);
      }
    } else {
      setIsScrolled(false);
      setHidden(false);
    }
  });

  const searchResults = searchQuery.trim()
    ? (products || []).filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.category && p.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 6)
    : [];

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleResultClick = (productId) => {
    setIsSearchOpen(false);
    setSearchQuery('');
    navigate(`/product/${productId}`);
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { to: '/aboutus', label: 'About Us' },
    { to: '/blog', label: 'Blog' },
  ];

  return (
    <>
      <motion.nav
        variants={{
          visible: { y: 0 },
          hidden: { y: "-100%" }
        }}
        animate={hidden ? "hidden" : "visible"}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className={`fixed w-full z-[100] top-0 px-4 sm:px-6 py-4 md:py-6 transition-colors duration-500 ${
          isScrolled || isMobileMenuOpen
            ? 'glass-panel !border-none !rounded-none py-3 md:py-4 bg-white/90'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center relative w-full">
          {/* Left Links - hidden on mobile, compact pills on tablet, full size on desktop */}
          <div className="hidden lg:flex flex-1 space-x-8 xl:space-x-12 text-xs xl:text-sm tracking-[0.2em] xl:tracking-[0.25em] uppercase font-semibold text-brand-darkSage">
            {navLinks.map(link => (
              <Link 
                key={link.to} 
                to={link.to} 
                className="relative group py-2 transition-colors duration-300 hover:text-brand-gold"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Tablet nav - compact pills, visible only at md-lg */}
          <div className="hidden md:flex lg:hidden items-center gap-1.5 flex-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-2.5 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider transition-all ${
                  location.pathname === link.to
                    ? 'bg-brand-darkSage text-white'
                    : 'text-brand-darkSage hover:bg-brand-darkSage/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Logo */}
          <div className="md:absolute md:left-1/2 md:transform md:-translate-x-1/2 z-10 flex items-center justify-center">
            <Link to="/" className="cursor-pointer" onClick={() => setIsMobileMenuOpen(false)}>
              <img
                src="https://media-manager-c.questera.ai/greta-media/29d79c4109d1c19c2e75790b8e50872c9a33c9d5ce79f6f43f60effe48b91ada6d4fe9244bdd866a6b5eb503d1d2a9a7/images/aW1hZ2UvcG5n/154eff52dc1f1c00de3fe58bdd4b1694.png"
                alt="Ceylanthro"
                className="h-12 sm:h-14 md:h-14 lg:h-20 xl:h-24 object-contain transition-all duration-300"
              />
            </Link>
          </div>

          {/* Right Icons */}
          <div className="flex items-center space-x-3 md:space-x-4 lg:space-x-6 text-brand-darkSage flex-1 justify-end">
            {user && (
              <button
                onClick={() => setIsSearchOpen(true)}
                className="hover:text-brand-gold transition-colors transform hover:scale-110 duration-300 p-1"
              >
                <SafeIcon icon={FiSearch} className="w-5 h-5" />
              </button>
            )}
            <Link to={user ? (profile?.role === 'admin' ? '/admin' : '/profile') : '/login'} className="hover:text-brand-gold transition-colors transform hover:scale-110 duration-300 p-1">
              <SafeIcon icon={FiUser} className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="hover:text-brand-gold transition-colors relative transform hover:scale-110 duration-300 p-1">
              <SafeIcon icon={FiShoppingBag} className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-gold text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow-lg">
                  {cartCount}
                </span>
              )}
            </Link>
            <button className="md:hidden hover:text-brand-gold transition-colors p-1" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Search Overlay */}
      <AnimatePresence>
        {isSearchOpen && user && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm"
            onClick={() => { setIsSearchOpen(false); setSearchQuery(''); }}
          >
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="absolute top-0 left-0 right-0 bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="max-w-3xl mx-auto px-6 py-6">
                <form onSubmit={handleSearchSubmit} className="flex items-center gap-3">
                  <div className="flex-1 relative">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-lightText w-5 h-5" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search products..."
                      autoFocus
                      className="w-full pl-12 pr-4 py-4 text-lg border border-gray-200 rounded-2xl focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 outline-none transition-all"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setIsSearchOpen(false); setSearchQuery(''); }}
                    className="p-3 text-gray-400 hover:text-brand-darkSage transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </form>

                {searchQuery.trim() && (
                  <div className="mt-4 max-h-[60vh] overflow-y-auto">
                    {searchResults.length > 0 ? (
                      <div className="space-y-2">
                        {searchResults.map(product => (
                          <button
                            key={product.id}
                            onClick={() => handleResultClick(product.id)}
                            className="w-full flex items-center gap-4 p-3 rounded-xl hover:bg-brand-sand/50 transition-colors text-left group"
                          >
                            <div className="w-14 h-14 bg-brand-sand/50 rounded-xl flex-shrink-0 flex items-center justify-center p-2 overflow-hidden">
                              <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-serif font-bold text-brand-darkSage truncate group-hover:text-brand-gold transition-colors">{product.name}</p>
                              <p className="text-xs text-brand-lightText capitalize">{product.category}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-bold text-brand-darkSage">Rs. {product.price.toFixed(2)}</p>
                            </div>
                            <FiArrowRight className="text-brand-lightText group-hover:text-brand-gold transition-colors flex-shrink-0" />
                          </button>
                        ))}
                        <div className="pt-3 border-t border-gray-100">
                          <button
                            onClick={handleSearchSubmit}
                            className="w-full text-center text-sm font-semibold text-brand-gold hover:text-brand-darkSage transition-colors py-2"
                          >
                            View all results for "{searchQuery}" →
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <p className="text-brand-lightText">No products found for "{searchQuery}"</p>
                        <button
                          onClick={handleSearchSubmit}
                          className="mt-2 text-sm font-semibold text-brand-gold hover:text-brand-darkSage transition-colors"
                        >
                          Search in shop anyway →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] bg-brand-sand/95 backdrop-blur-lg pt-24 px-6 flex flex-col md:hidden h-screen"
          >
            <div className="flex flex-col space-y-8 mt-10">
              {navLinks.map(link => (
                <Link key={link.to} to={link.to} onClick={() => setIsMobileMenuOpen(false)} className="text-3xl font-serif text-brand-darkSage hover:text-brand-gold transition-colors text-left">
                  {link.to === '/shop' ? 'Shop Collection' : link.label}
                </Link>
              ))}

              <div className="w-full h-px bg-brand-darkSage/10 my-4" />

              {user && (
                <form onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) { navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`); setIsMobileMenuOpen(false); setSearchQuery(''); } }} className="flex gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="flex-1 px-4 py-3 border border-brand-darkSage/20 rounded-xl bg-white/50 text-brand-darkSage placeholder-brand-lightText outline-none focus:border-brand-gold"
                  />
                  <button type="submit" className="px-4 py-3 bg-brand-darkSage text-white rounded-xl hover:bg-brand-gold transition-colors">
                    <SafeIcon icon={FiSearch} className="w-5 h-5" />
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;